/* eslint-disable no-shadow */
import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import PropTypes, { any } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import ColumnHeader, { SortType } from '../../../../../components/ColumnHeader';
import Pagination from '../../../../../components/Pagination';
import PreRegInvitedPlayer from './PreRegInvitedPlayer';
import Img from '../../../../../components/Img';
import PreRegInvitesFilters from './PreRegInvitesFilters';

import * as eventProfileActions from '../../../../../actions/event-profile-actions';
import { showInfoPopup } from '../../../../../actions/notification-actions';
import { MaximumPreRegInvitesPerPage, MinimumNumberOfPages, PreRegInviteStatuses } from '../../../../../utils/eventConstants';
import { getMinDateAllowedForPreRegInviteExtend } from '../../../../../utils/events';
import { NotificationPopUpLabels } from '../../../../../utils/notificationConstants';

const PreRegInvitesTabContent = forwardRef((props, ref) => {
    const {
        eventId,
        preRegInvites,
        isPickleballEvent,
        preRegFilterOptions,
        utrTypeId,
        sportTypeId,
        isCurrentMemberAdmin,
        toggleMessagePopUp,
    } = props;
    const { event } = useSelector(({ eventProfile }) => {
        const { event } = eventProfile;
        return {
            event,
        };
    });
    const [invitesList, setInvitesList] = useState(preRegInvites?.data?.players ?? []);
    const [queryData, setQueryData] = useState({
        currentPage: 1,
        filters: null,
        sortData: {
            type: SortType.DESC,
            column: 'dateSent',
        },
    });
    const [allPlayersInRangeSelected, setAllPlayersInRangeSelected] = useState(false);
    const totalPages = useMemo(() => {
        return preRegInvites?.data?.total ? Math.ceil(preRegInvites.data.total / MaximumPreRegInvitesPerPage) : MinimumNumberOfPages;
    }, [preRegInvites?.data]);
    const dispatch = useDispatch();
    const hasMinimumNumberOfPages = totalPages === MinimumNumberOfPages;

    useEffect(() => {
        setInvitesList(preRegInvites?.data?.players);
        setAllPlayersInRangeSelected(false);
    }, [preRegInvites?.data?.players]);

    useEffect(() => {
        dispatch(eventProfileActions.getPreRegInviteFilters(eventId));
    }, [eventId]);

    const refreshInviteList = useCallback(() => {
        if (eventId && queryData?.currentPage && !!queryData?.filters) {
            getUpdatedInviteList(eventId, queryData.currentPage, queryData.filters, queryData.sortData);
        }
    }, [eventId, queryData]);

    useEffect(() => {
        refreshInviteList();
    }, [refreshInviteList]);

    const getUpdatedInviteList = async (id, cPage, filters, sortData) => {
        await dispatch(eventProfileActions.getPreRegistrationInviteList(id, filters, sortData, cPage));
    };

    const onTogglePlayerSelect = (playerToToggle) => {
        const updatedInvitesList = invitesList.map((player) => {
            if (player?.preRegistrationInviteId === playerToToggle?.preRegistrationInviteId) {
                return { ...player, isSelected: !player?.isSelected };
            }
            return player;
        });
        const isAllPlayersInPageSelected = updatedInvitesList.every(player => player?.isSelected);

        setInvitesList(updatedInvitesList);
        setAllPlayersInRangeSelected(hasMinimumNumberOfPages && isAllPlayersInPageSelected);
    };

    const onMessageSelectedInvitePlayers = async () => {
        // Change this to memberIds once api is updated
        let selectedPlayerMemberIds = [];
        if (allPlayersInRangeSelected) {
            selectedPlayerMemberIds = await dispatch(eventProfileActions.getMemberIdsFromPreRegInvites(eventId, getBulkSelectionData()));
        } else {
            selectedPlayerMemberIds = invitesList
                .filter(player => player?.isSelected)
                .map(player => player?.id);
        }

        if (selectedPlayerMemberIds.length > 0) {
            toggleMessagePopUp([...new Set(selectedPlayerMemberIds)]);
        }
    };

    const gridLayout =
     [
        '1fr',
        '6fr',
        '4fr',
        '3fr',
        '2fr',
        '2fr',
        '2.5fr',
        '3fr',
        '3fr',
        '0.5fr',
    ];

    const handlePageChange = useCallback((page) => {
        setQueryData({
            ...queryData,
            currentPage: page,
        });
    }, [queryData]);

    const onUpdateFilters = useCallback((filters) => {
        setQueryData({
            ...queryData,
            currentPage: 1,
            filters,
        });
    }, [queryData]);

    const isAtLeastOnePlayerInPageSelected = invitesList?.some((player) => player.isSelected);
    const selectedPlayerCount = useMemo(() => {
        if (allPlayersInRangeSelected) {
            return preRegInvites?.data?.total;
        }

        return invitesList?.filter(player => player?.isSelected).length || 0;
    }, [invitesList]);
    const hasRegisteredPlayersInSelection = useMemo(() => {
        return invitesList?.some(player => player.isSelected && player.statusId === PreRegInviteStatuses.REGISTERED);
    }, [invitesList]);

    const onDeselectAllPlayersInCurrentPage = () => {
        setInvitesList(invitesList.map(player => ({ ...player, isSelected: false })));
        setAllPlayersInRangeSelected(false);
    };

    const onSelectAllPlayersInCurrentPage = () => {
        const updatedInvitesList = invitesList.map(player => ({ ...player, isSelected: true }));
        const isAllPlayersInPageSelected = updatedInvitesList.every(player => player?.isSelected);
        setInvitesList(updatedInvitesList);
        setAllPlayersInRangeSelected(hasMinimumNumberOfPages && isAllPlayersInPageSelected);
    };

    const onSelectAllPlayersInRange = () => {
        setAllPlayersInRangeSelected(true);
        setInvitesList(invitesList.map(player => ({ ...player, isSelected: true })));
    };

    const getBulkSelectionData = () => {
        if (allPlayersInRangeSelected) {
            return {
                inviteIds: [],
                ...queryData.filters,
            };
        }

        if (isAtLeastOnePlayerInPageSelected) {
            return {
                inviteIds: invitesList?.filter(player => player?.isSelected).map(player => player?.preRegistrationInviteId),
                search: null,
                divisionCategoryIds: [],
                medalTiers: [],
                statusIds: [],
                sentDates: [],
                expirationDates: [],
            };
        }

        return {
            inviteIds: [],
            search: null,
            divisionCategoryIds: [],
            medalTiers: [],
            statusIds: [],
            sentDates: [],
            expirationDates: [],
        };
    };

    const onConfirmResendInvites = () => {
        dispatch(eventProfileActions.bulkResendPreRegistrationInvites(eventId, getBulkSelectionData(), queryData));
        onDeselectAllPlayersInCurrentPage();
    };

    const onResendSelectedInvites = () => {
        if (hasRegisteredPlayersInSelection) {
            return;
        }

        dispatch(showInfoPopup({
            title: 'Resend Invites?',
            message: (
                <>
                    <div className="fs14 mb8">Are you sure you want to resend the selected invite(s)?</div>
                    <div className="fs14 text-dark">
                        Note: Any selected registered invite(s) will be excluded from this action.
                    </div>
                </>
            ),
            showCancelButton: true,
            cancelButtonLabel: 'Cancel',
            showConfirmButton: true,
            confirmButtonLabel: 'Resend',
            onConfirmClick: onConfirmResendInvites,
            showCloseButton: true,
            noIcon: true,
        }));
    };

    const onConfirmDeleteInvites = () => {
        dispatch(eventProfileActions.bulkDeletePreRegistrationInvites(eventId, getBulkSelectionData(), queryData));
        onDeselectAllPlayersInCurrentPage();
    };

    const onConfirmDeleteSpecificInvite = (inviteId) => {
        const data = {
            inviteIds: [inviteId],
            search: null,
            divisionCategoryIds: [],
            medalTiers: [],
            statusIds: [],
            sentDates: [],
            expirationDates: [],
        };
        dispatch(eventProfileActions.bulkDeletePreRegistrationInvites(eventId, data, queryData));
    };

    const onDeleteSelectedInvites = () => {
        dispatch(showInfoPopup({
            title: 'Delete Invites?',
            message: 'Are you sure you want to delete the selected invite(s)?',
            showCancelButton: true,
            cancelButtonLabel: NotificationPopUpLabels.CANCEL,
            showConfirmButton: true,
            confirmButtonLabel: NotificationPopUpLabels.DELETE,
            onConfirmClick: onConfirmDeleteInvites,
            showCloseButton: true,
            noIcon: true,
        }));
    };

    const handleDeleteInvite = async (inviteId) => {
        dispatch(showInfoPopup({
            title: 'Delete Invite?',
            message: 'Are you sure you want to delete the invite?',
            showCancelButton: true,
            cancelButtonLabel: NotificationPopUpLabels.CANCEL,
            showConfirmButton: true,
            confirmButtonLabel: NotificationPopUpLabels.DELETE,
            onConfirmClick: () => onConfirmDeleteSpecificInvite(inviteId),
            showCloseButton: true,
            noIcon: true,
        }));
    };

    const minDateToAllowForExtend = useMemo(() => {
        return getMinDateAllowedForPreRegInviteExtend(
            event.data?.eventSchedule?.timeZone,
            totalPages,
            allPlayersInRangeSelected,
            preRegFilterOptions.expirationDates,
            invitesList,
        );
    }, [event.data, invitesList, allPlayersInRangeSelected, totalPages, preRegFilterOptions]);

    const maxDateToAllowForExtend = useMemo(() => {
        return moment
            .utc(event.data?.eventSchedule?.registrationStartUtc)
            .tz(event.data?.eventSchedule?.timeZone?.value)
            .endOf('day')
            .subtract(1, 'day');
    }, [event.data]);

    const onConfirmExtendInvite = (updatedDate) => {
        const data = getBulkSelectionData();
        data.extendedExpirationDate = updatedDate.format('YYYY-MM-DD');
        dispatch(eventProfileActions.bulkExtendPreRegistrationInvites(eventId, data, queryData));
        onDeselectAllPlayersInCurrentPage();
    };

    const onExtendDateSelected = (newDate) => {
        if (hasRegisteredPlayersInSelection) {
            return;
        }

        const updatedDate = moment(newDate);
        const extendedDateString = updatedDate.format('MMM DD, YYYY');

        dispatch(showInfoPopup({
            title: 'Extend Invite?',
            message: (
                <>
                    <div className="fs14 mb8">
                        {`Are you sure you want to extend the invite for the selected player(s) till ${extendedDateString}?`}
                    </div>
                    <div className="fs14 text-dark">
                        Note: Any selected registered invite(s) will be excluded from this action.
                    </div>
                </>
            ),
            showCancelButton: true,
            cancelButtonLabel: 'Cancel',
            showConfirmButton: true,
            confirmButtonLabel: 'Extend',
            onConfirmClick: () => onConfirmExtendInvite(updatedDate),
            showCloseButton: true,
            noIcon: true,
        }));
    };

    const onUpdateSortType = useCallback((column, sortType) => {
        setQueryData({
            ...queryData,
            sortData: {
                type: sortType,
                column,
            },
        });
    }, [queryData]);

    const actionsItemClassName = 'd-flex aic link icon mr16';

    useImperativeHandle(ref, () => ({
        refreshInviteList,
    }), [refreshInviteList]);

    return (
        <div className="mt24">
            {preRegFilterOptions && (
                <PreRegInvitesFilters
                    preRegFilterOptions={preRegFilterOptions}
                    onUpdateFilters={onUpdateFilters}
                />
            )}
            <div className="d-flex text-small text-medium division-tab-header-v4">
                <div className="flex-grow">
                    <div className="row">
                        <div className={`col-12`}>
                            {!isAtLeastOnePlayerInPageSelected && (
                                <div className="d-grid w-100" style={{ gridTemplateColumns: gridLayout.map(fr => `minmax(0, ${fr})`).join(' ') }}>
                                    <div
                                        className="checkbox-wrapper d-flex align-items-center pointer"
                                        onClick={onSelectAllPlayersInCurrentPage}
                                        data-testid="pre-reg-invites.select-all-checkbox"
                                    >
                                        <div className="checkbox-check">
                                            <Img src="/img/icons/checkmark-icon.svg" />
                                        </div>
                                    </div>
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Player name"
                                        initialSort={SortType.ASC}
                                        isSortable
                                        sortType={queryData?.sortData?.type}
                                        isSelected={queryData?.sortData?.column === 'playerName'}
                                        onClick={(sortType) => onUpdateSortType('playerName', sortType)}
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Category"
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Medal"
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName={isPickleballEvent ? 'UTR-P' : 'UTR'}
                                        imgUrl="/img/v3/icons/icon-singles-black.svg"
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName={isPickleballEvent ? 'UTR-P' : 'UTR'}
                                        imgUrl="/img/v3/icons/icon-doubles-black.svg"
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Status"
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Date sent"
                                        initialSort={SortType.DESC}
                                        isSortable
                                        sortType={queryData?.sortData?.type}
                                        isSelected={queryData?.sortData?.column === 'dateSent'}
                                        onClick={(sortType) => onUpdateSortType('dateSent', sortType)}
                                    />
                                    <ColumnHeader
                                        className="mr4"
                                        columnName="Expiration Date"
                                        initialSort={SortType.DESC}
                                        isSortable
                                        sortType={queryData?.sortData?.type}
                                        isSelected={queryData?.sortData?.column === 'expirationDate'}
                                        onClick={(sortType) => onUpdateSortType('expirationDate', sortType)}
                                    />
                                    <ColumnHeader
                                        columnName={``}
                                    />
                                </div>
                            )}
                            {isAtLeastOnePlayerInPageSelected && (
                                <div className="d-grid d-flex w-100">
                                    <div
                                        className="checkbox-wrapper d-flex aic pointer"
                                        onClick={onDeselectAllPlayersInCurrentPage}
                                        data-testid="pre-reg-invites.deselect-all-checkbox"
                                    >
                                        <div className="checkbox-check checked">
                                            <Img src="/img/icons/check-indeterminate.svg" />
                                        </div>
                                    </div>
                                    <div className="text-dark mr16">
                                        {selectedPlayerCount} selected
                                    </div>
                                    {isAtLeastOnePlayerInPageSelected && !allPlayersInRangeSelected && (
                                        <div
                                            className="text-teal clickable mr16"
                                            onClick={onSelectAllPlayersInRange}
                                            data-testid="pre-reg-invites.select-all-range-btn"
                                        >
                                            {`Select All ${preRegInvites?.data?.total} Players`}
                                        </div>
                                    )}
                                    <div className="mr16">|</div>
                                    <div
                                        className={`${actionsItemClassName} clickable`}
                                        onClick={onMessageSelectedInvitePlayers}
                                        data-testid="pre-reg-invites.message-selected-btn"
                                    >
                                        <Img
                                            className="mr8"
                                            alt="message"
                                            src={'/img/v4/message.svg'}
                                        />
                                        Message Selected Players
                                    </div>
                                    <div
                                        className={`${actionsItemClassName} ${hasRegisteredPlayersInSelection ? 'disabled' : 'clickable'}`}
                                        onClick={onResendSelectedInvites}
                                        data-testid="pre-reg-invites.resend-invite-btn"
                                    >
                                        <Img
                                            className="mr8"
                                            alt="message"
                                            src={hasRegisteredPlayersInSelection
                                                ? '/img/v4/icon-clickable-grey.svg'
                                                : '/img/v4/icon-clickable-blue.svg'}
                                        />
                                        Resend Invite
                                    </div>
                                    <div
                                        className={`${actionsItemClassName} ${hasRegisteredPlayersInSelection ? 'disabled' : 'clickable'}`}
                                    >
                                        <DatePicker
                                            disabled={hasRegisteredPlayersInSelection}
                                            minDate={minDateToAllowForExtend}
                                            maxDate={maxDateToAllowForExtend}
                                            openToDate={minDateToAllowForExtend}
                                            onSelect={onExtendDateSelected}
                                            customInput={(
                                                <div className="d-flex aic link icon">
                                                    <Img
                                                        className="mr8"
                                                        style={{ marginTop: '-4px' }}
                                                        alt="calendar"
                                                        src={hasRegisteredPlayersInSelection
                                                            ? '/img/v4/icon-calendar-grey.svg'
                                                            : '/img/v4/icon-calendar-blue.svg'}
                                                    />
                                                    Extend Invite
                                                </div>
                                            )}
                                        />
                                    </div>
                                    <div
                                        className={`${actionsItemClassName} clickable`}
                                        onClick={onDeleteSelectedInvites}
                                        data-testid="pre-reg-invites.delete-invite-btn"
                                    >
                                        <Img
                                            className="mr8"
                                            alt="message"
                                            src="/img/v4/icon-delete-blue.svg"
                                        />
                                        Delete Invite
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={`p-rel ${preRegInvites?.loading && 'opacity05'}`}>
                {invitesList?.map((invitedPlayer) => {
                        return (
                            <PreRegInvitedPlayer
                                key={`pre-reg-invited-player-${invitedPlayer.playerId}-${invitedPlayer.preRegistrationInviteId}`}
                                player={invitedPlayer}
                                gridLayout={gridLayout}
                                isPickleballEvent={isPickleballEvent}
                                utrTypeId={utrTypeId}
                                sportTypeId={sportTypeId}
                                isCurrentMemberAdmin={isCurrentMemberAdmin}
                                onDeleteInvite={handleDeleteInvite}
                                onTogglePlayerSelect={onTogglePlayerSelect}
                            />
                        );
                    })}
            </div>
            {preRegInvites?.loading && (
                <div className="row divisions-big">
                    <div className="col">
                        Loading player invites...
                    </div>
                </div>
            )}
            {!preRegInvites?.loading && !invitesList?.length && (
                <div className="row divisions-big">
                    <div className="col">
                        No players are matching your filter criteria.
                    </div>
                </div>
            )}
            {totalPages > 1 && (
                <Pagination
                    lastPage={totalPages}
                    currentPage={queryData?.currentPage ?? 1}
                    onClick={handlePageChange}
                />
            )}
        </div>
    );
});

PreRegInvitesTabContent.propTypes = {
    preRegInvites: PropTypes.objectOf(any).isRequired,
    isPickleballEvent: PropTypes.bool,
    eventId: PropTypes.number,
    preRegFilterOptions: PropTypes.objectOf(any).isRequired,
    utrTypeId: PropTypes.number,
    sportTypeId: PropTypes.number,
    isCurrentMemberAdmin: PropTypes.bool,
    toggleMessagePopUp: PropTypes.func.isRequired,
};
PreRegInvitesTabContent.defaultProps = {
    isPickleballEvent: true,
    eventId: 0,
    utrTypeId: 1,
    sportTypeId: 2,
    isCurrentMemberAdmin: false,
};

export default PreRegInvitesTabContent;
